// The four buckets an expense travels through in the payroll view, and the
// rules for moving between them. The table view derives its sections from
// getExpenseStage; the kanban board additionally uses evaluateExpenseTransition
// to decide what a drag is allowed to do.
//
// The flow is one step at a time, forwards:
//
//   Pending Approval -> Approved, Awaiting Reimbursement -> Approved and Paid -> Invoiced
//
// Nothing skips a bucket, so the only three forward moves that do anything are
// "approve", "mark paid" and "mark invoiced". Each records something the next
// one depends on, which is why a skip is refused outright rather than offered
// as a confirmation.
//
// Backwards is different. It records nothing — it erases: a revert throws away
// the invoice date, the payment date or the approval itself. So a backward drop
// is refused by default and offered as an override, which says what will be
// erased and needs a second click. PUT /expenses/:id/revert-stage does the
// work, and can undo any number of steps at once because clearing two dates is
// no harder than clearing one.
//
// The last bucket is only for money we bill back: an expense that isn't marked
// customer reimbursable ends its life in Approved and Paid.

export const STAGE_PENDING = 'pending';
export const STAGE_AWAITING = 'awaiting';
export const STAGE_PAID = 'paid';
export const STAGE_INVOICED = 'invoiced';

export const EXPENSE_STAGES = [
  { id: STAGE_PENDING, title: 'Pending Approval' },
  { id: STAGE_AWAITING, title: 'Approved, Awaiting Reimbursement' },
  { id: STAGE_PAID, title: 'Approved and Paid' },
  { id: STAGE_INVOICED, title: 'Invoiced' },
];

// Only these can reach the Invoiced bucket, and the customer they are billed to
// is what the invoice is raised against.
export const isCustomerReimbursable = (expense) => Boolean(expense?.customer_reimbursable);

// A contract can limit what we are allowed to bill a customer for an item, so
// the money that comes back can be less than the money that went out. The cap
// is recorded when the expense is invoiced and never replaces `amount`: the
// board goes on showing what we paid, and only the totals opt into the cap.
export const getInvoiceCap = (expense) => {
  const cap = Number(expense?.invoice_cap_amount);
  return expense?.invoice_cap_amount == null || !Number.isFinite(cap) ? null : cap;
};

export const isInvoiceCapped = (expense) => getInvoiceCap(expense) != null;

// What the expense was actually worth to us: the cap where there is one, the
// full amount otherwise.
export const getInvoicedAmount = (expense) => {
  const cap = getInvoiceCap(expense);
  return cap == null ? Number(expense?.amount) || 0 : cap;
};

const STAGE_ORDER = EXPENSE_STAGES.map((stage) => stage.id);

export const getStageIndex = (stageId) => STAGE_ORDER.indexOf(stageId);

export const getStageTitle = (stageId) =>
  EXPENSE_STAGES.find((stage) => stage.id === stageId)?.title || stageId;

// A company card purchase (purchased_by_me) was already paid by the company, so
// it never awaits reimbursement — approving it settles it. New approvals get a
// paid_on date from the server; this also keeps expenses approved before that
// behaviour existed out of the reimbursement queue.
export const isSettledOnApproval = (expense) => Boolean(expense?.purchased_by_me);

// Returns null for anything that does not belong on the board at all (rejected,
// cancelled, or a draft that slipped through).
export const getExpenseStage = (expense) => {
  const status = String(expense?.status || '').toLowerCase();
  if (status === 'pending') return STAGE_PENDING;
  if (status !== 'approved') return null;
  // The invoice date is only ever set on a settled, reimbursable expense, so it
  // is the last word on where the expense sits.
  if (expense?.invoiced_on) return STAGE_INVOICED;
  if (expense?.paid_on || isSettledOnApproval(expense)) return STAGE_PAID;
  return STAGE_AWAITING;
};

// Spells out what a revert throws away, listed newest first so the sentence
// reads in the order the expense travelled. The board puts this in front of
// anyone reaching for the override, because none of it can be recovered by
// dragging the expense forward again — the dates would have to be re-entered.
const describeRevert = (fromStage, toStage) => {
  const from = getStageIndex(fromStage);
  const to = getStageIndex(toStage);
  const erased = [];

  if (from >= getStageIndex(STAGE_INVOICED) && to < getStageIndex(STAGE_INVOICED)) {
    erased.push('the invoice date and any invoice cap');
  }
  if (from >= getStageIndex(STAGE_PAID) && to < getStageIndex(STAGE_PAID)) {
    erased.push('the payment date');
  }
  if (to === STAGE_PENDING) {
    erased.push('the approval, sending the expense back to whoever reviews it');
  }

  if (erased.length === 0) return '';
  if (erased.length === 1) return `This clears ${erased[0]}.`;
  return `This clears ${erased.slice(0, -1).join(', ')} and ${erased[erased.length - 1]}.`;
};

/**
 * Decides what dropping `expense` on `toStage` should do.
 *
 * @returns {{
 *   allowed: boolean,
 *   action: 'approve'|'markPaid'|'markInvoiced'|null,
 *   title: string|null,
 *   reason: string|null,
 *   override: {toStage: string, label: string}|null,
 * }}
 *   `action` names the flow the board should start; `reason` explains a
 *   refusal. A refusal with no reason is a drag that simply went nowhere. An
 *   `override` is a refusal the person dragging is allowed to overrule, and
 *   carries the label for the button that does it.
 */
export const evaluateExpenseTransition = ({ expense, toStage, currentEmployeeId }) => {
  const fromStage = getExpenseStage(expense);
  const refuse = (title, reason) => ({ allowed: false, action: null, title, reason, override: null });

  if (!fromStage || getStageIndex(toStage) < 0) {
    return refuse('Move not available', 'This expense is not on the payroll board.');
  }

  // Dropped back where it started; the board just snaps it home.
  if (fromStage === toStage) {
    return { allowed: false, action: null, title: null, reason: null, override: null };
  }

  if (getStageIndex(toStage) < getStageIndex(fromStage)) {
    // Approving a company card purchase settles it on the spot, so there is no
    // reimbursement left owing and no queue for it to wait in. Undoing the
    // approval is the only way back past this point for one of these.
    if (toStage === STAGE_AWAITING && isSettledOnApproval(expense)) {
      return refuse(
        'Company card purchases are settled on approval',
        'The company already paid for this, so there is no reimbursement for it to be awaiting. '
          + `Drop it on ${getStageTitle(STAGE_PENDING)} to undo the approval instead.`,
      );
    }

    return {
      allowed: false,
      action: null,
      title: 'Backward move needs an override',
      reason: `${describeRevert(fromStage, toStage)} None of it comes back by dragging the expense `
        + 'forward again — the dates would have to be entered afresh. Override this to correct a mistake.',
      override: { toStage, label: `Override, move to ${getStageTitle(toStage)}` },
    };
  }

  // Checked before the self-expense rule because it is a property of the
  // expense rather than of whoever is dragging it: there is no other approver
  // who could put a non-billable expense in this bucket either.
  if (toStage === STAGE_INVOICED && !isCustomerReimbursable(expense)) {
    return refuse(
      'Not customer reimbursable',
      'Only expenses marked customer reimbursable can be invoiced. '
        + 'Edit the expense to set the customer it is billed to if this is a mistake.',
    );
  }

  const isSelfExpense = Boolean(currentEmployeeId)
    && String(expense?.employee_id || '') === String(currentEmployeeId);
  if (isSelfExpense) {
    return refuse('You cannot approve your own request', 'Ask another finance approver to move this expense.');
  }

  if (fromStage === STAGE_PENDING && toStage === STAGE_AWAITING) {
    return { allowed: true, action: 'approve', title: null, reason: null, override: null };
  }

  if (fromStage === STAGE_AWAITING && toStage === STAGE_PAID) {
    return { allowed: true, action: 'markPaid', title: null, reason: null, override: null };
  }

  if (fromStage === STAGE_PAID && toStage === STAGE_INVOICED) {
    return { allowed: true, action: 'markInvoiced', title: null, reason: null, override: null };
  }

  // Everything left skips a bucket: Pending -> Paid, Pending -> Invoiced or
  // Awaiting -> Invoiced. Each step records something the next one depends on,
  // so the drop names the one that comes next instead.
  const nextStage = EXPENSE_STAGES[getStageIndex(fromStage) + 1];
  return refuse(
    'One step at a time',
    `${getStageTitle(fromStage)} → ${getStageTitle(toStage)} skips a step. `
      + `Drop it on ${getStageTitle(nextStage?.id)} first.`,
  );
};
